import { Col, Row } from 'antd';
import { useField, useFormikContext } from 'formik';
import { isBoolean } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FinanceProductBasedOnField } from '.';
import { AmountUnit, type InterestRateSettingsPayload } from '../../../../../api/types';
import ConfirmSwitchField from '../../../../../components/fields/ConfirmSwitchField';
import RadioGroupField from '../../../../../components/fields/RadioGroupField';
import FormFields from '../../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../../utilities/useSystemSwitchData';
import { useFinanceProductDetailContext } from '../../FinanceProductDetailContext';
import { InterestRateType } from '../../shared/typings';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../index';

const colSpan = { lg: 8, xs: 24 };

const InterestRateFixed = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    return (
        <Col {...colSpan}>
            <FormFields.InputNumberField
                {...t('financeProductDetails:fields.interestRateDefault', {
                    returnObjects: true,
                })}
                disabled={disabled}
                max={100}
                min={0}
                name="interestRate.default"
                required
            />
        </Col>
    );
};

type InterestRateRangeProps<T extends Pick<InterestRateSettingsPayload, 'min' | 'max'>> = {
    interestRate: T;
    disabled?: boolean;
};

const InterestRateRange = <T extends Pick<InterestRateSettingsPayload, 'min' | 'max'>>({
    interestRate,
    disabled = false,
}: InterestRateRangeProps<T>) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { min = 0, max = 0 } = interestRate;

    return (
        <>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.interestRateMin', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    max={100}
                    min={0}
                    name="interestRate.min"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.interestRateMax', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    max={100}
                    min={0}
                    name="interestRate.max"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.interestRateStep', {
                        returnObjects: true,
                    })}
                    disabled={min >= max || disabled}
                    max={100}
                    min={0}
                    name="interestRate.step"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.interestRateDefault', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    max={100}
                    min={0}
                    name="interestRate.default"
                    required
                />
            </Col>
        </>
    );
};

const UnitContainer = styled.div`
    & .ant-form-item .ant-form-item-control {
        width: fit-content;
        min-width: 4.625rem;
        text-align: center;
    }
`;

type InterestRateTableProps<T extends Pick<InterestRateSettingsPayload, 'unit' | 'isGenerateStepsInTable'>> = {
    interestRate: T;
    disabled?: boolean;
};

const InterestRateTable = <T extends Pick<InterestRateSettingsPayload, 'unit' | 'isGenerateStepsInTable'>>({
    interestRate,
    disabled = false,
}: InterestRateTableProps<T>) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { bank } = useFinanceProductDetailContext();

    const { yesNoSwitch } = useSystemSwitchData();

    const [, , tableHelpers] = useField({ name: 'interestRate.table' });
    const onIsGenerateStepsInTableChange = () => {
        tableHelpers.setValue([]);
    };

    const [isGenerateStepsInTable, , { setValue }] = useField({
        name: 'interestRate.isGenerateStepsInTable',
    });
    // when switch interest type to table, give a default value
    useEffect(() => {
        if (!isBoolean(isGenerateStepsInTable.value)) {
            setValue(true);
        }
    }, []);

    const { amountUnitOptions, fieldDisplaySettingsOptions } = useFinanceProductOptions(bank?.module?.company?.id);

    const max = useMemo(
        () => (interestRate?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER),
        [interestRate?.unit]
    );

    return (
        <>
            <Col {...colSpan}>
                <FinanceProductBasedOnField disabled />
            </Col>
            <Col {...colSpan}>
                <FormFields.SelectField
                    {...t('financeProductDetails:fields.interestRateTableUnit', { returnObjects: true })}
                    disabled={disabled}
                    name="interestRate.unit"
                    options={amountUnitOptions}
                    required
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <RadioGroupField
                    {...t('financeProductDetails:fields.interestRateViewable', { returnObjects: true })}
                    disabled={disabled}
                    name="interestRate.isViewable"
                    options={fieldDisplaySettingsOptions}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.interestRateTableMin', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    max={max}
                    min={0}
                    name="interestRate.min"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.interestRateTableMax', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    max={max}
                    min={0}
                    name="interestRate.max"
                    required
                />
            </Col>

            <Col {...colSpan}>
                <ConfirmSwitchField
                    {...t('financeProductDetails:fields.generateStepsInTable', { returnObjects: true })}
                    confirmTitle={t('financeProductDetails:messages.switchIsGenerateStepsInTable.title')}
                    disabled={disabled}
                    name="interestRate.isGenerateStepsInTable"
                    onChange={onIsGenerateStepsInTableChange}
                    {...yesNoSwitch}
                />
            </Col>

            {interestRate?.isGenerateStepsInTable && (
                <Col {...colSpan}>
                    <FormFields.InputNumberField
                        {...t('financeProductDetails:fields.interestRateTableGroupingStep', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        max={Number.MAX_SAFE_INTEGER}
                        min={0}
                        name="interestRate.step"
                        required
                    />
                </Col>
            )}
        </>
    );
};

const InterestRate = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { interestRateTypeOptions, fieldDisplaySettingsOptions } = useFinanceProductOptions();

    const { values } = useFormikContext<FinanceProductFormValues>();

    const { interestRate } = values;

    const content = useMemo(() => {
        switch (interestRate?.type) {
            case InterestRateType.Range:
                return <InterestRateRange disabled={disabled} interestRate={interestRate} />;

            case InterestRateType.Table:
                return <InterestRateTable disabled={disabled} interestRate={interestRate} />;

            case InterestRateType.Fixed:
                return <InterestRateFixed disabled={disabled} />;

            default:
                throw new Error(`Interest Type (${interestRate?.type}) not supported`);
        }
    }, [disabled, interestRate]);

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <RadioGroupField
                    {...t('financeProductDetails:fields.type', { returnObjects: true })}
                    disabled={disabled}
                    name="interestRate.type"
                    options={interestRateTypeOptions}
                    required
                />
            </Col>
            {content}

            {interestRate?.type !== InterestRateType.Table && (
                <Col {...colSpan}>
                    <RadioGroupField
                        {...t('financeProductDetails:fields.interestRateViewable', { returnObjects: true })}
                        disabled={disabled}
                        name="interestRate.isViewable"
                        options={fieldDisplaySettingsOptions}
                        required
                    />
                </Col>
            )}

            <Col {...colSpan}>
                <RadioGroupField
                    {...t('financeProductDetails:fields.displayTotalInterestPayable', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="interestRate.displayTotalInterestPayable"
                    options={fieldDisplaySettingsOptions}
                    required
                />
            </Col>
        </Row>
    );
};

export default InterestRate;
